import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	differenceInCalendarDays,
	endOfDay,
	startOfDay,
	startOfMonth,
	subDays,
} from "date-fns";

import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

import { DailyChart, HourChart, QueueChart, RatingChart, ReasonChart } from "./Chart";
import StatCard from "./StatCard";
import Title from "./Title";
import { formatDuration } from "./format";

const MAX_RANGE_DAYS = 366;

const useStyles = makeStyles(theme => ({
	container: {
		paddingTop: theme.spacing(3),
		paddingBottom: theme.spacing(4),
	},
	header: {
		marginBottom: theme.spacing(2),
	},
	subtitle: {
		color: theme.palette.text.secondary,
	},
	filters: {
		marginBottom: theme.spacing(2),
	},
	progress: {
		height: 3,
		marginBottom: theme.spacing(2),
		visibility: "hidden",
	},
	progressActive: {
		visibility: "visible",
	},
	panel: {
		padding: theme.spacing(2),
		height: "100%",
	},
	empty: {
		color: theme.palette.text.secondary,
		padding: theme.spacing(4, 0),
		textAlign: "center",
	},
	section: {
		marginTop: theme.spacing(3),
	},
	rightCell: {
		textAlign: "right",
	},
}));

const computeRange = (preset, customStart, customEnd) => {
	const now = new Date();

	switch (preset) {
		case "today":
			return { start: startOfDay(now), end: endOfDay(now) };
		case "7d":
			return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
		case "30d":
			return { start: startOfDay(subDays(now, 29)), end: endOfDay(now) };
		case "month":
			return { start: startOfMonth(now), end: endOfDay(now) };
		default: {
			const start = new Date(`${customStart}T00:00:00`);
			const end = new Date(`${customEnd}T23:59:59.999`);
			return { start, end };
		}
	}
};

const rangeError = ({ start, end }) => {
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
		return i18n.t("dashboard.page.errors.invalidDates");
	}
	if (end < start) return i18n.t("dashboard.page.errors.endBeforeStart");
	if (differenceInCalendarDays(end, start) > MAX_RANGE_DAYS) {
		return i18n.t("dashboard.page.errors.tooLong");
	}
	return null;
};

const toInputDate = date => {
	const pad = value => String(value).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const Dashboard = () => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const isAdmin = user.profile === "admin";

	const [preset, setPreset] = useState("7d");
	const [customStart, setCustomStart] = useState(toInputDate(subDays(new Date(), 6)));
	const [customEnd, setCustomEnd] = useState(toInputDate(new Date()));
	const [queueId, setQueueId] = useState("");
	const [attendantId, setAttendantId] = useState("");

	const [queues, setQueues] = useState([]);
	const [attendants, setAttendants] = useState([]);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);

	const range = useMemo(
		() => computeRange(preset, customStart, customEnd),
		[preset, customStart, customEnd]
	);
	const error = rangeError(range);
	const startIso = Number.isNaN(range.start.getTime()) ? "" : range.start.toISOString();
	const endIso = Number.isNaN(range.end.getTime()) ? "" : range.end.toISOString();

	useEffect(() => {
		if (!isAdmin) return;

		const loadFilters = async () => {
			try {
				const [queueResponse, userResponse] = await Promise.all([
					api.get("/queue"),
					api.get("/users/", { params: { pageNumber: 1 } }),
				]);
				setQueues(queueResponse.data);
				setAttendants(userResponse.data.users);
			} catch (err) {
				toastError(err);
			}
		};
		void loadFilters();
	}, [isAdmin]);

	useEffect(() => {
		if (error) return undefined;

		let cancelled = false;
		setLoading(true);

		const timer = setTimeout(async () => {
			try {
				const { data: result } = await api.get("/dashboard", {
					params: {
						startDate: startIso,
						endDate: endIso,
						queueId: queueId || undefined,
						attendantId: attendantId || undefined,
						tzOffset: new Date().getTimezoneOffset(),
					},
				});
				if (!cancelled) setData(result);
			} catch (err) {
				if (!cancelled) toastError(err);
			} finally {
				if (!cancelled) setLoading(false);
			}
		}, 300);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [startIso, endIso, queueId, attendantId, error]);

	const t = key => i18n.t(`dashboard.page.${key}`);
	const totals = data?.totals;
	const noQueue = t("noQueue");

	const queueData = (data?.byQueue || []).map(item => ({
		...item,
		key: String(item.queueId),
		name: item.name || noQueue,
	}));

	const quality = data?.quality;
	const reasonData = (quality?.closeReasons || []).map(item => ({
		...item,
		name: item.name || t("noReason"),
	}));
	const hasHours = (data?.byHour || []).some(value => value > 0);

	const dash = "—";
	const num = value => (value === undefined || value === null ? dash : value);

	return (
		<Container maxWidth="lg" className={classes.container}>
			<div className={classes.header}>
				<Typography variant="h5" component="h1">
					{t("title")}
				</Typography>
				<Typography variant="body2" className={classes.subtitle}>
					{data?.scope === "own" || !isAdmin ? t("subtitleOwn") : t("subtitleAll")}
				</Typography>
			</div>

			<Grid container spacing={2} className={classes.filters} alignItems="center">
				<Grid item xs={12} sm={6} md={3}>
					<TextField
						select
						fullWidth
						size="small"
						variant="outlined"
						label={t("period.label")}
						value={preset}
						onChange={e => setPreset(e.target.value)}
					>
						<MenuItem value="today">{t("period.today")}</MenuItem>
						<MenuItem value="7d">{t("period.last7")}</MenuItem>
						<MenuItem value="30d">{t("period.last30")}</MenuItem>
						<MenuItem value="month">{t("period.month")}</MenuItem>
						<MenuItem value="custom">{t("period.custom")}</MenuItem>
					</TextField>
				</Grid>

				{preset === "custom" && (
					<>
						<Grid item xs={6} sm={3} md={2}>
							<TextField
								fullWidth
								size="small"
								variant="outlined"
								type="date"
								label={t("period.from")}
								InputLabelProps={{ shrink: true }}
								value={customStart}
								onChange={e => setCustomStart(e.target.value)}
							/>
						</Grid>
						<Grid item xs={6} sm={3} md={2}>
							<TextField
								fullWidth
								size="small"
								variant="outlined"
								type="date"
								label={t("period.to")}
								InputLabelProps={{ shrink: true }}
								value={customEnd}
								onChange={e => setCustomEnd(e.target.value)}
							/>
						</Grid>
					</>
				)}

				{isAdmin && (
					<>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								select
								fullWidth
								size="small"
								variant="outlined"
								label={t("filters.queue")}
								value={queueId}
								onChange={e => setQueueId(e.target.value)}
							>
								<MenuItem value="">{t("filters.allQueues")}</MenuItem>
								{queues.map(queue => (
									<MenuItem key={queue.id} value={queue.id}>
										{queue.name}
									</MenuItem>
								))}
							</TextField>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								select
								fullWidth
								size="small"
								variant="outlined"
								label={t("filters.attendant")}
								value={attendantId}
								onChange={e => setAttendantId(e.target.value)}
							>
								<MenuItem value="">{t("filters.allAttendants")}</MenuItem>
								{attendants.map(attendant => (
									<MenuItem key={attendant.id} value={attendant.id}>
										{attendant.name}
									</MenuItem>
								))}
							</TextField>
						</Grid>
					</>
				)}
			</Grid>

			{error && (
				<Typography color="error" variant="body2" gutterBottom>
					{error}
				</Typography>
			)}

			<LinearProgress
				className={`${classes.progress} ${loading && !error ? classes.progressActive : ""}`}
			/>

			<Grid container spacing={2}>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						label={t("cards.open.label")}
						hint={t("cards.open.hint")}
						value={num(totals?.open)}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						label={t("cards.pending.label")}
						hint={t("cards.pending.hint")}
						value={num(totals?.pending)}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						label={t("cards.unanswered.label")}
						hint={t("cards.unanswered.hint")}
						value={num(totals?.unanswered)}
						color={totals?.unanswered > 0 ? "#ed6c02" : undefined}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						label={t("cards.created.label")}
						hint={t("cards.created.hint")}
						value={num(totals?.created)}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						label={t("cards.closed.label")}
						hint={t("cards.closed.hint")}
						value={num(totals?.closed)}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						label={t("cards.firstResponse.label")}
						hint={t("cards.firstResponse.hint")}
						value={formatDuration(data?.firstResponse?.averageSeconds)}
						caption={
							data?.firstResponse?.sample
								? `${data.firstResponse.sample} ${t("cards.conversations")}`
								: undefined
						}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						label={t("cards.handling.label")}
						hint={t("cards.handling.hint")}
						value={formatDuration(data?.handling?.averageSeconds)}
						caption={
							data?.handling?.sample
								? `${data.handling.sample} ${t("cards.conversations")}`
								: undefined
						}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						label={t("cards.rating.label")}
						hint={t("cards.rating.hint")}
						value={
							quality?.rating?.average === null || quality?.rating?.average === undefined
								? dash
								: `${String(quality.rating.average).replace(".", ",")}/5`
						}
						caption={
							quality?.rating?.count
								? `${quality.rating.count} ${t("cards.ratings")}`
								: undefined
						}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						label={t("cards.ratingRate.label")}
						hint={t("cards.ratingRate.hint")}
						value={
							quality?.rating?.responseRate === null || quality?.rating?.responseRate === undefined
								? dash
								: `${quality.rating.responseRate}%`
						}
						caption={
							quality?.rating?.requested
								? `${quality.rating.count}/${quality.rating.requested}`
								: undefined
						}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<StatCard
						label={t("cards.newContacts.label")}
						hint={t("cards.newContacts.hint")}
						value={num(totals?.newContacts)}
					/>
				</Grid>
			</Grid>

			<Grid container spacing={2} className={classes.section}>
				<Grid item xs={12} md={7}>
					<Paper variant="outlined" className={classes.panel}>
						<Title>{t("charts.perDay")}</Title>
						{data && data.totals.created > 0 ? (
							<DailyChart data={data.byDay} label={t("cards.created.label")} />
						) : (
							<Typography className={classes.empty}>{t("empty")}</Typography>
						)}
					</Paper>
				</Grid>
				<Grid item xs={12} md={5}>
					<Paper variant="outlined" className={classes.panel}>
						<Title>{t("charts.perQueue")}</Title>
						{queueData.length ? (
							<QueueChart data={queueData} label={t("cards.created.label")} />
						) : (
							<Typography className={classes.empty}>{t("empty")}</Typography>
						)}
					</Paper>
				</Grid>
			</Grid>

			<Grid container spacing={2} className={classes.section}>
				<Grid item xs={12} md={6}>
					<Paper variant="outlined" className={classes.panel}>
						<Title>{t("charts.byHour")}</Title>
						{hasHours ? (
							<HourChart data={data.byHour} label={t("cards.created.label")} />
						) : (
							<Typography className={classes.empty}>{t("empty")}</Typography>
						)}
					</Paper>
				</Grid>
				<Grid item xs={12} md={6}>
					<Paper variant="outlined" className={classes.panel}>
						<Title>{t("charts.ratings")}</Title>
						{quality?.rating?.count ? (
							<RatingChart
								distribution={quality.rating.distribution}
								label={t("cards.ratings")}
							/>
						) : (
							<Typography className={classes.empty}>{t("noRatings")}</Typography>
						)}
					</Paper>
				</Grid>
				<Grid item xs={12}>
					<Paper variant="outlined" className={classes.panel}>
						<Title>{t("charts.closeReasons")}</Title>
						{reasonData.length ? (
							<ReasonChart data={reasonData} label={t("cards.closed.label")} />
						) : (
							<Typography className={classes.empty}>{t("empty")}</Typography>
						)}
					</Paper>
				</Grid>
			</Grid>

			{isAdmin && data?.byAttendant?.length > 0 && (
				<Paper variant="outlined" className={`${classes.panel} ${classes.section}`}>
					<Title>{t("charts.perAttendant")}</Title>
					<div style={{ overflowX: "auto" }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>{t("table.attendant")}</TableCell>
									<TableCell className={classes.rightCell}>
										{t("table.created")}
									</TableCell>
									<TableCell className={classes.rightCell}>
										{t("table.closed")}
									</TableCell>
									<TableCell className={classes.rightCell}>
										{t("table.firstResponse")}
									</TableCell>
									<TableCell className={classes.rightCell}>
										{t("table.rating")}
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.byAttendant.map(row => (
									<TableRow key={String(row.userId)}>
										<TableCell>{row.name || t("noAttendant")}</TableCell>
										<TableCell className={classes.rightCell}>{row.tickets}</TableCell>
										<TableCell className={classes.rightCell}>{row.closed}</TableCell>
										<TableCell className={classes.rightCell}>
											{formatDuration(row.firstResponseSeconds)}
										</TableCell>
										<TableCell className={classes.rightCell}>
											{row.ratingAverage === null || row.ratingAverage === undefined
												? dash
												: `${String(row.ratingAverage).replace(".", ",")} (${row.ratingCount})`}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</Paper>
			)}
		</Container>
	);
};

export default Dashboard;
